import React, { useEffect, useState } from 'react';
import TwBackdrop from '../../../components/TwBackdrop/TwBackdrop';
import Box from '@mui/material/Box';
import TwTable, { TwTableColumn } from '../../../components/TwTable/TwTable';
import TwPaginator from '../../../components/TwPaginator/TwPaginator';
import { BaseQueryParams } from '../../../providers/Global.model';
import { TwTableData } from '../../../components/TwDataGrid/TwDataGrid';
import StringUtils from '../../../utils/stringUtils';
import TwGenericMenu, {
  TwGenericMenuOptions
} from '../../../components/TwGenericMenu/TwGenericMenu';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import TwNameColumn from '../../../components/TwTable/TwNameColumn';
import {
  useDeleteDivisionMutation,
  useSearchDivisionsQuery
} from '../../../redux/api/divisions/divisionsApi';
import { IDivisionsResponse } from '../../../redux/api/divisions/divisions.model';
import TwEditCompaniesModal from '../EditCompaniesModal/TwEditCompaniesModal';
import { CompaniesEntities } from '../Companies';
import TwDialog from '../../../components/TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import { ReactComponent as WarningSign } from '../../../assets/warning-sign.svg';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import useAuth from '../../../providers/Auth/Auth.provider';

const Divisions: React.FC = (): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const { addNotification } = useNotifications();
  const [page, setPage] = useState<number>(1);
  const [tableData, setTableData] = React.useState<TwTableData[]>([]);
  const [selectedDivision, setSelectedDivision] = useState<string>('');
  const [openEditDivision, setOpenEditDivision] = useState<boolean>(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false);
  const [deleteDivision] = useDeleteDivisionMutation();
  const itemsPerPage = 10;
  const columns: TwTableColumn[] = [
    { id: 'name', label: 'NAME', minWidth: 150 },
    { id: 'address', label: 'ADDRESS', minWidth: 150 },
    { id: 'email', label: 'EMAIL', minWidth: 150 },
    { id: 'phone', label: 'PHONE NUMBER', minWidth: 100 },
    { id: 'menu', label: '', maxWidth: 10 }
  ];
  const [pageParams, setPageParams] = useState<BaseQueryParams>({
    offset: 0,
    limit: itemsPerPage,
    deleted: false
  });

  // RTK Query hook for divisions
  const {
    data: divisions = { content: [], total: 0 },
    isLoading,
    error
  } = useSearchDivisionsQuery({
    pageParams
  });

  const handlePageChange = (page: number): void => {
    setPage(page);
  };

  const closeEditDivision = (): void => {
    setOpenEditDivision(false);
    setSelectedDivision('');
  };

  const Menu: React.FC<{ id: string; disableDelete: boolean }> = ({
    id,
    disableDelete
  }): JSX.Element => {
    const options: TwGenericMenuOptions[] = [
      {
        label: 'Edit',
        action: (): void => {
          setSelectedDivision(id);
          setOpenEditDivision(true);
        }
      }
    ];

    // if disableDelete === false then add the delete option
    if (!disableDelete) {
      options.push({
        label: 'Delete',
        className: 'clients__delete',
        action: (): void => {
          setSelectedDivision(id);
          setDeleteConfirmationOpen(true);
        }
      });
    }

    return (
      <div className="clients__options">
        <TwGenericMenu options={options} />
      </div>
    );
  };

  const formatTableData = (): void => {
    const divisionsList: TwTableData[] = [];
    divisions.content.forEach((division: IDivisionsResponse) => {
      const tableItem: TwTableData = {
        id: division.id,
        name: (
          <TwNameColumn
            name={division.name}
            subLabel={division.is_default_division ? 'Your company' : undefined}
          />
        ),
        address: division.location?.full_address ?? '',
        email: division.email_address,
        phone: StringUtils.formatPhoneNumber(division.phone ?? '', division.phone_ext ?? ''),

        menu: <Menu id={division.id} disableDelete={division.is_default_division} />
      };

      divisionsList.push(tableItem);
    });

    setTableData(divisionsList);
  };

  const onDeleteDivision = async (id: string): Promise<void> => {
    deleteDivision(id)
      .then((res) => {
        addNotification({
          message: 'Division has been deleted',
          variant: 'success'
        });

        setDeleteConfirmationOpen(false);
        setSelectedDivision('');
      })
      .catch((err) => {
        logRocketTrack('Division Management', {
          action: 'Delete division',
          severity: 'medium',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        setDeleteConfirmationOpen(false);
        setSelectedDivision('');
        addNotification({
          message: 'Error at deleting division, please try again',
          variant: 'error'
        });
      });
  };

  useEffect(() => {
    if (!error) {
      formatTableData();
    } else {
      addNotification({
        variant: 'error',
        message: 'Error loading divisions'
      });
    }
  }, [divisions, error]);

  useEffect(() => {
    const newOffset = (page - 1) * itemsPerPage;
    setPageParams({ ...pageParams, offset: newOffset, limit: itemsPerPage });
  }, [page]);

  return (
    <>
      {isLoading ? (
        <TwBackdrop isOpen={isLoading} />
      ) : (
        <Box sx={{ paddingTop: '1rem' }}>
          <TwTable columns={columns} data={tableData} />
          <TwPaginator
            currentPage={page}
            items={divisions.total}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
          <TwEditCompaniesModal
            open={openEditDivision}
            entity={CompaniesEntities.division}
            entityId={selectedDivision}
            onClose={closeEditDivision}
          />

          <TwDialog
            size="xs"
            title="Delete division?"
            open={deleteConfirmationOpen}
            onClickClose={(): void => setDeleteConfirmationOpen(false)}
            actions={[
              <TwButton
                key="1"
                type={TwButtonType.secondary}
                label="Cancel"
                action={(): void => setDeleteConfirmationOpen(false)}
              />,
              <TwButton
                key="2"
                type={TwButtonType.negative}
                label="Delete"
                action={(): Promise<void> => onDeleteDivision(selectedDivision)}
              />
            ]}
          >
            <Box component="div" className="clients__confirmation-dialog-content">
              <WarningSign />
              <span className="clients__confirmation-dialog-text">
                Are you sure you want to delete this division? You can’t undo this action.
              </span>
            </Box>
          </TwDialog>
        </Box>
      )}
    </>
  );
};

export default Divisions;
